// Public technical deep-dive page, linked from the landing-page FAQ.
// Accessible signed-out so it can be shared as a direct URL.

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSeoHead } from "../lib/useSeoHead";

const FAQ_TITLE =
  "How MTG Oracle works — RAG, DuckDB, LanceDB, Cohere, Claude | Technical deep dive";
const FAQ_DESCRIPTION =
  "Technical deep dive into MTG Oracle: a retrieval-augmented agent stack on AWS Bedrock. DuckDB + LanceDB two-store corpus, hybrid BM25 + Cohere Embed v4 retrieval fused via RRF, Cohere Rerank 3.5 cross-encoder, and a multi-model Claude Opus / Sonnet / Haiku pipeline gated by versioned prompts and an A/B eval harness.";
const FAQ_URL = "https://themtgoracle.com/technical-faq";

const FAQ_JSONLD: Array<Record<string, unknown>> = [
  {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline:
      "How MTG Oracle works — retrieval-augmented agent stack on AWS Bedrock",
    description: FAQ_DESCRIPTION,
    url: FAQ_URL,
    mainEntityOfPage: FAQ_URL,
    inLanguage: "en-US",
    author: { "@type": "Organization", name: "MTG Oracle" },
    publisher: {
      "@type": "Organization",
      name: "MTG Oracle",
      logo: {
        "@type": "ImageObject",
        url: "https://themtgoracle.com/logo.svg",
      },
    },
    about: [
      "Retrieval-Augmented Generation",
      "DuckDB",
      "LanceDB",
      "BM25",
      "Cohere Embed v4",
      "Reciprocal Rank Fusion",
      "Cohere Rerank 3.5",
      "Claude Opus 5.5",
      "AWS Bedrock",
      "Magic: The Gathering deck building",
    ],
    keywords:
      "RAG, retrieval augmented generation, DuckDB, LanceDB, BM25, Cohere Embed, Reciprocal Rank Fusion, Cohere Rerank, Claude, AWS Bedrock, MTG, Magic the Gathering, deck builder, prompt engineering, A/B evals",
    image: "https://themtgoracle.com/og-image.png",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://themtgoracle.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Technical FAQ",
        item: FAQ_URL,
      },
    ],
  },
];

export default function TechnicalFaq() {
  useSeoHead({
    title: FAQ_TITLE,
    description: FAQ_DESCRIPTION,
    canonical: FAQ_URL,
    jsonLd: FAQ_JSONLD,
  });
  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <header className="border-b border-border/60">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="MTG Oracle" width={28} height={28} />
            <span className="font-semibold tracking-tight">MTG Oracle</span>
          </Link>
          <Link to="/" className="btn-ghost text-sm inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium text-accent mb-3 uppercase tracking-wider">
          Technical deep dive
        </p>
        <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.1]">
          What's going on under the hood?
        </h1>
        <p className="mt-6 text-lg text-fg-muted leading-relaxed">
          The Oracle is a retrieval-augmented agent stack on AWS Bedrock: a
          two-store corpus, hybrid BM25 + dense retrieval fused by
          Reciprocal Rank Fusion, a cross-encoder rerank pass, and a
          multi-model inference pipeline gated by versioned prompts and
          an A/B eval harness. The sections below cover each layer.
        </p>

        <div className="mt-12 space-y-10 text-sm md:text-base text-fg-muted leading-relaxed">
          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Why DuckDB + LanceDB (the two-store split)
            </h2>
            <p className="mt-3">
              Card data lives in two stores side by side.{" "}
              <strong className="text-fg">DuckDB</strong> is the
              structured/analytical layer for card metadata: columnar
              OLAP with real SQL, perfect for pre-filtering the
              Standard-legal pool by color identity, mana cost, type, and
              archetype tags before any vector math happens.{" "}
              <strong className="text-fg">LanceDB</strong> is the vector
              layer: a zero-ops, file-based vector DB built on the Lance
              columnar format, so there's no Pinecone bill and no extra
              service to run. The whole corpus lives in object storage and can be rebuilt
              independently and deterministically.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Embedding + retrieval pipeline
            </h2>
            <p className="mt-3">
              Retrieval is hybrid. A filtered candidate set comes out of
              DuckDB via SQL, then{" "}
              <strong className="text-fg">BM25 lexical search</strong> and{" "}
              <strong className="text-fg">
                dense vector search (Cohere Embed v4)
              </strong>{" "}
              run in parallel against LanceDB. The two ranked lists fuse
              via{" "}
              <strong className="text-fg">Reciprocal Rank Fusion</strong>{" "}
              (k=60, tunable weights), and the top pool is handed to{" "}
              <strong className="text-fg">Cohere Rerank 3.5</strong> on
              Bedrock for a final cross-encoder pass. BM25 catches exact
              card names and keywords that dense vectors routinely miss;
              dense catches semantic intent like "graveyard recursion
              that doesn't fold to exile"; RRF sidesteps the problem of
              calibrating incompatible score scales; the reranker cleans
              up the tail. Cards are enriched upstream with
              Sonnet-generated archetype/synergy tags <em>before</em>{" "}
              embedding, so the vectors encode play patterns, not just
              rules text.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Prompt engineering + optimization
            </h2>
            <p className="mt-3">
              System prompts are{" "}
              <strong className="text-fg">versioned in code</strong>{" "}
              (currently v4) with human-readable notes on every bump, and
              every saved deck records which prompt produced it, so a
              regression three weeks later is fully reproducible. Prompt
              changes are gated by an{" "}
              <strong className="text-fg">A/B eval harness</strong>: a
              composite score (50% tournament proximity on card overlap
              vs real tournament decks, plus 50% LLM-as-judge rubric)
              where a new version has to beat the old by ≥ +1.0{" "}
              <em>without regressing</em> on any sub-metric.
            </p>
            <p className="mt-3">
              Concrete wins from this loop: v2 added a mandatory{" "}
              <code className="text-fg">get_meta_snapshot</code> call on
              open-ended briefs with a tournament-count floor, which
              killed the "agent reaches for mono-red every time" failure
              mode. v3 silenced dollar-figure commentary unless the user
              explicitly asks about budget. v4 inlined source URLs on
              every exemplar citation so saved-deck markdown auto-links
              attribution. A cheap{" "}
              <strong className="text-fg">Haiku classifier</strong> also
              categorizes incoming briefs (
              <code className="text-fg">
                open | archetype | budget | mechanic | contrarian
              </code>
              ) to gate whether exemplar retrieval even runs. The eval
              data showed community exemplars <em>hurt</em> rubric scores
              on "open" briefs, so the gate skips them entirely there.
            </p>
            <p className="mt-3">
              The actual deck-building principles encoded in the prompt
              — Mike Flores's Misassignment of Role, Reid Duke's
              Band-Aid anti-pattern, PVDDR's potential-vs-realized
              power, Frank Karsten's mana-base regression — are
              documented separately in the{" "}
              <Link
                to="/deck-building-method"
                className="text-accent hover:underline"
              >
                deck-building method
              </Link>{" "}
              page, with citations back to the named pro sources.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl text-fg font-semibold tracking-tight">
              Model routing + evals
            </h2>
            <p className="mt-3">
              Inference is multi-model by design:{" "}
              <strong className="text-fg">Claude Sonnet 5.5</strong> for
              deck building and analysis,{" "}
              <strong className="text-fg">Opus 5.5</strong> for high-stakes
              reasoning, and{" "}
              <strong className="text-fg">Haiku 4.5</strong> as a cheap
              classifier. Each tier is used only where it earns its cost.
              Evals are three-layered: LLM-as-judge rubric scoring
              (dual-judge, Sonnet + Opus, with a maxΔ outlier check for
              judge drift), a tournament-proximity scorer, and option to run{" "}
              <strong className="text-fg">
                Forge headless-gameplay simulations
              </strong>
              : actual rules-complete matches for ground-truth W-L-D, with natural language summarization of the outcomes (win conditions, weakness, unexpected outcomes, etc).
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-border/60">
          <Link
            to="/"
            className="text-sm text-accent hover:underline inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}
